/**
 * Where the device credential lives between visits — **IndexedDB, never `localStorage`**.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS AT ALL
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `10-data-model.md` §"device_credential" and spec 09's storage row both say the same thing:
 * the opaque device credential is *"stored in **IndexedDB** on the device, never
 * `localStorage`"*, and the API's own route comment repeats it. `localStorage` is readable
 * synchronously by anything running on the origin and survives in a shape trivially dumped
 * by an extension or a pasted console line; IndexedDB is at least an explicit, asynchronous,
 * structured store. Neither is a vault — the real protection is that the credential is
 * short-lived, revocable from Configuration, and bound to one device — but the spec picks
 * the better of the two and so do we.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SHAPE, AND WHY IT IS THIS SHAPE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **IndexedDB is asynchronous; an `Authorization` header is not.** Every API call needs
 *     the credential synchronously, at the moment the request is assembled. So this module
 *     keeps a module-level cache — one string, in memory — and the store is read into it
 *     exactly once, by {@link loadCredential}, during the session provider's boot phase and
 *     **before** the first `GET /auth/me`. After that the cache is the read path and the
 *     store is only ever written to.
 *
 *  2. **Writes are cache-first, store-second.** {@link storeCredential} and
 *     {@link clearCredential} stay synchronous — the redeem screen and the sign-out button
 *     both need the next request to be right *immediately* — and the store is brought into
 *     line behind them. A failed store write means the credential does not survive a reload;
 *     it never means the current session is holding the wrong one.
 *
 *  3. **The credential never leaves this module except into a header.** It is not returned
 *     to React state, not rendered, not logged, and not put in an error message. The one
 *     export that hands it back, {@link storedCredential}, exists for the one caller in
 *     `client.ts` that builds `Authorization: Bearer …`.
 *
 *  4. **The seam is injectable so the tests can be honest.** jsdom ships no IndexedDB at all,
 *     so a test that exercised the real implementation would be testing a shim. Instead the
 *     store is an interface with two implementations — the real, dependency-free IndexedDB
 *     one below (raw `indexedDB`, no library) and an in-memory double the suite installs.
 *     The cache, the migration and the ordering — the parts that actually carry the bug risk
 *     — are the same code in both cases.
 *
 *  5. **One-time migration.** Earlier builds of this front end wrote the credential to
 *     `localStorage` under {@link LEGACY_CREDENTIAL_STORAGE_KEY}. A device that signed in on
 *     one of those must not be signed out by the upgrade, so the boot read moves the value
 *     across and deletes the `localStorage` copy — deleting only **after** the IndexedDB
 *     write has succeeded, because the alternative failure mode is a device that has thrown
 *     away its only credential and cannot sign itself back in.
 */

/** The `localStorage` key earlier builds used. Read once, on migration, then removed. */
export const LEGACY_CREDENTIAL_STORAGE_KEY = "hockeytd.credential";

/** The IndexedDB database, object store and record key. One device, one credential. */
export const CREDENTIAL_DB_NAME = "hockeytd";
export const CREDENTIAL_STORE_NAME = "credential";
export const CREDENTIAL_RECORD_KEY = "device";

/**
 * The storage seam.
 *
 * Deliberately three methods and no more: this is not a key/value abstraction, it is "the
 * one place the one credential is kept". Every method is allowed to fail softly — a browser
 * in private mode may deny storage entirely, and the honest consequence of that is a device
 * that must be signed in again on the next visit, not a white screen.
 */
export interface CredentialStorage {
  read: () => Promise<string | null>;
  write: (credential: string) => Promise<void>;
  remove: () => Promise<void>;
}

/* ── The real implementation: raw IndexedDB, no library ───────────────────── */

function hasIndexedDb(): boolean {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(CREDENTIAL_DB_NAME, 1);
    request.onupgradeneeded = (): void => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CREDENTIAL_STORE_NAME)) {
        database.createObjectStore(CREDENTIAL_STORE_NAME);
      }
    };
    request.onsuccess = (): void => {
      resolve(request.result);
    };
    request.onerror = (): void => {
      reject(request.error ?? new Error("IndexedDB could not be opened."));
    };
    request.onblocked = (): void => {
      reject(new Error("IndexedDB is blocked by another open version of this app."));
    };
  });
}

function runTransaction<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDatabase().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(CREDENTIAL_STORE_NAME, mode);
        const request = work(transaction.objectStore(CREDENTIAL_STORE_NAME));
        request.onsuccess = (): void => {
          resolve(request.result);
        };
        request.onerror = (): void => {
          reject(request.error ?? new Error("The credential store refused the operation."));
        };
        transaction.oncomplete = (): void => {
          database.close();
        };
        transaction.onabort = (): void => {
          database.close();
          reject(transaction.error ?? new Error("The credential store aborted."));
        };
      }),
  );
}

/**
 * The browser store. Every operation swallows its own failure for the reason given at the
 * top of the file: storage can be denied, and the API's 401 already says what to do about a
 * device that is holding nothing.
 */
export function indexedDbCredentialStorage(): CredentialStorage {
  return {
    read: async (): Promise<string | null> => {
      if (!hasIndexedDb()) return null;
      try {
        const value = await runTransaction<unknown>("readonly", (store) =>
          store.get(CREDENTIAL_RECORD_KEY),
        );
        return typeof value === "string" && value !== "" ? value : null;
      } catch {
        return null;
      }
    },
    write: async (credential: string): Promise<void> => {
      if (!hasIndexedDb()) return;
      try {
        await runTransaction("readwrite", (store) =>
          store.put(credential, CREDENTIAL_RECORD_KEY),
        );
      } catch {
        // Nothing is thrown. See rule 2 above: the in-memory cache is already correct, so
        // this session works; only its survival across a reload was lost.
      }
    },
    remove: async (): Promise<void> => {
      if (!hasIndexedDb()) return;
      try {
        await runTransaction("readwrite", (store) => store.delete(CREDENTIAL_RECORD_KEY));
      } catch {
        // A credential that cannot be removed also could not have been written.
      }
    },
  };
}

/* ── The in-memory double, for tests ──────────────────────────────────────── */

export interface MemoryCredentialStorage extends CredentialStorage {
  /** Synchronous inspection, so a test can assert what was persisted without awaiting. */
  peek: () => string | null;
  /** Seed a device that was already signed in before this render. */
  seed: (credential: string) => void;
}

export function memoryCredentialStorage(initial: string | null = null): MemoryCredentialStorage {
  let held: string | null = initial;
  return {
    read: (): Promise<string | null> => Promise.resolve(held),
    write: (credential: string): Promise<void> => {
      held = credential;
      return Promise.resolve();
    },
    remove: (): Promise<void> => {
      held = null;
      return Promise.resolve();
    },
    peek: (): string | null => held,
    seed: (credential: string): void => {
      held = credential;
    },
  };
}

/* ── The cache, the seam and the operations over both ─────────────────────── */

let storage: CredentialStorage = indexedDbCredentialStorage();

/**
 * The synchronous read path (rule 1). `null` means "nothing held", which is also what an
 * un-booted module reports — which is why {@link loadCredential} runs before the first
 * authenticated call rather than beside it.
 */
let cache: string | null = null;

/**
 * How many times the cache has been written since the module loaded — every store and every clear.
 *
 * Security QA pass 2 (18 September 2026) found the boot read racing a redemption: a device opening
 * its first personal link stores the new credential while {@link loadCredential} is still waiting on
 * IndexedDB, and the read then finished and put what it had found — nothing — back in the cache, so
 * the next request went out without a credential and the device was signed out again. A read that
 * began before a write knows less than the write does. The count lets it tell.
 */
let writes = 0;

/** Test seam. Installs a store and empties the cache, so no credential crosses a test. */
export function setCredentialStorage(next: CredentialStorage): void {
  storage = next;
  cache = null;
}

/** Convenience for the suite: a fresh in-memory store, installed and returned. */
export function installMemoryCredentialStorage(
  initial: string | null = null,
): MemoryCredentialStorage {
  const fake = memoryCredentialStorage(initial);
  setCredentialStorage(fake);
  return fake;
}

/** Put the real IndexedDB store back. Used by nothing but a test that swapped it out. */
export function resetCredentialStorage(): void {
  setCredentialStorage(indexedDbCredentialStorage());
}

/** The legacy `localStorage` value, or null. Never throws — storage can be denied. */
function legacyCredential(): string | null {
  try {
    const value = window.localStorage.getItem(LEGACY_CREDENTIAL_STORAGE_KEY);
    return value === null || value === "" ? null : value;
  } catch {
    return null;
  }
}

function forgetLegacyCredential(): void {
  try {
    window.localStorage.removeItem(LEGACY_CREDENTIAL_STORAGE_KEY);
  } catch {
    // Nothing to do; a value that cannot be removed could not have been written either.
  }
}

/**
 * Read the store into the cache, once, at boot — **before** `GET /auth/me`.
 *
 * Also performs the one-time `localStorage` → IndexedDB migration described in rule 5. The
 * IndexedDB value wins when both exist (it is the newer of the two writers), and the legacy
 * copy is deleted either way, but only ever after the value it held is safely somewhere else.
 */
export async function loadCredential(): Promise<string | null> {
  const writesBefore = writes;
  const stored = await storage.read();
  // A credential stored — or a sign-out — while the read was out is newer than anything it found.
  if (writes !== writesBefore) return cache;
  const legacy = legacyCredential();

  if (stored !== null && stored !== "") {
    if (legacy !== null) forgetLegacyCredential();
    cache = stored;
    return cache;
  }

  if (legacy !== null) {
    await storage.write(legacy);
    // Only now: a device that has thrown away its only credential cannot sign itself back in.
    if ((await storage.read()) === legacy) forgetLegacyCredential();
    // The same, across the migration's own write and read: a newer credential, or a sign-out, is
    // not overwritten in the cache by the pre-migration copy.
    if (writes !== writesBefore) return cache;
    cache = legacy;
    return cache;
  }

  cache = null;
  return null;
}

/**
 * The credential, for the one caller that puts it in an `Authorization` header.
 *
 * Synchronous and cache-only by design — see rule 1. It is never rendered and never returned
 * to React state.
 */
export function storedCredential(): string | null {
  return cache;
}

/**
 * Write the device credential, once, on redemption.
 *
 * The value goes from the redeem response straight into the cache and on into IndexedDB. It
 * is never returned to a caller, never held in React state and never rendered.
 */
export function storeCredential(credential: string): void {
  writes += 1;
  cache = credential;
  void storage.write(credential);
}

/** Forget the device credential — sign-out, and every 401 that means it no longer works. */
export function clearCredential(): void {
  writes += 1;
  cache = null;
  void storage.remove();
  // A device upgrading mid-session may still carry the pre-migration copy; a sign-out that
  // left it behind would resurrect the credential on the next boot.
  forgetLegacyCredential();
}
